/**
 * Enums and constants for the admin application
 */

/**
 * User roles
 */
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  MANAGER = 'manager',
  SUPPORT = 'support',
  VIEWER = 'viewer',
}

/**
 * User statuses
 */
export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  SUSPENDED = 'suspended',
}

/**
 * User login methods
 */
export enum LoginMethod {
  PHONE = 'phone',
  EMAIL = 'email',
  EMAIL_AND_PHONE = 'emailAndPhone',
}

/**
 * Buyer statuses
 */
export enum BuyerStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING_APPROVAL = 'pending_approval',
  REJECTED = 'rejected',
  SUSPENDED = 'suspended',
}

/**
 * Buyer user roles - roles that users can have within a buyer organization
 */
export enum BuyerUserRole {
  ADMIN = 'buyerAdmin',
  STAFF = 'buyerStaff',
  CONTRIBUTOR = 'buyerViewer',
  SUPPORT = 'buyerSupport',
  DEFAULT = 'Default',
}

/**
 * Supplier statuses
 */
export enum SupplierStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING_APPROVAL = 'pending_approval',
  REJECTED = 'rejected',
  SUSPENDED = 'suspended',
  ONBOARDING = 'onboarding',
}

/**
 * Order statuses
 */
export enum OrderStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  PROCESSING = 'processing',
  SHIPPED = 'shipped',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
}

/**
 * Payment statuses
 */
export enum PaymentStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
}

/**
 * Product statuses
 */
export enum ProductStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  OUT_OF_STOCK = 'out_of_stock',
  DISCONTINUED = 'discontinued',
}

/**
 * Priority levels
 */
export enum Priority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

/**
 * Business types
 */
export enum BusinessType {
  RESTAURANT = 'restaurant',
  CAFE = 'cafe',
  BAR = 'bar',
  HOTEL = 'hotel',
  CATERING = 'catering',
  RETAIL = 'retail',
  DISTRIBUTOR = 'distributor',
  MANUFACTURER = 'manufacturer',
  FARM = 'farm',
  OTHER = 'other',
}

/**
 * Payment terms
 */
export enum PaymentTerms {
  NET_7 = 'net_7',
  NET_15 = 'net_15',
  NET_30 = 'net_30',
  NET_60 = 'net_60',
  NET_90 = 'net_90',
  COD = 'cod',
  PREPAID = 'prepaid',
}

/**
 * Currencies
 */
export enum Currency {
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
  CAD = 'CAD',
  AUD = 'AUD',
}

/**
 * Languages
 */
export enum Language {
  EN = 'en',
  ES = 'es',
  FR = 'fr',
  DE = 'de',
  IT = 'it',
}

/**
 * Locale codes
 */
export enum LocaleCode {
  CZECH = 'cs-CZ',
  DUTCH_BELGIUM = 'nl-BE',
  ENGLISH_US = 'en-US',
  ENGLISH_GB = 'en-GB',
  ENGLISH_AE = 'en-AE',
  ENGLISH_SA = 'en-SA',
  ENGLISH_CA = 'en-CA',
  ARABIC_SAUDI_ARABIA = 'ar-SA',
  FRENCH = 'fr-FR',
  GERMAN = 'de-DE',
  ITALIAN = 'it-IT',
  PORTUGUESE_BRAZIL = 'pt-BR',
  SPANISH = 'es-ES',
}
/**
 * Units of measurement
 */
export enum Unit {
  PIECE = 'piece',
  POUND = 'pound',
  KILOGRAM = 'kilogram',
  OUNCE = 'ounce',
  GRAM = 'gram',
  LITER = 'liter',
  MILLILITER = 'milliliter',
  GALLON = 'gallon',
  CASE = 'case',
  BOX = 'box',
  PACK = 'pack',
  DOZEN = 'dozen',
}

/**
 * Notification types
 */
export enum NotificationType {
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
}

/**
 * Communication channels
 */
export enum CommunicationChannel {
  EMAIL = 'email',
  SMS = 'sms',
  PUSH = 'push',
  IN_APP = 'in_app',
  SLACK = 'slack',
  WEBHOOK = 'webhook',
}

/**
 * File types
 */
export enum FileType {
  IMAGE = 'image',
  DOCUMENT = 'document',
  SPREADSHEET = 'spreadsheet',
  PDF = 'pdf',
  CSV = 'csv',
  JSON = 'json',
  XML = 'xml',
}

/**
 * Integration types
 */
export enum IntegrationType {
  API = 'api',
  WEBHOOK = 'webhook',
  FILE_UPLOAD = 'file_upload',
  EMAIL = 'email',
  FTP = 'ftp',
  SFTP = 'sftp',
}

/**
 * Report types
 */
export enum ReportType {
  SALES = 'sales',
  ORDERS = 'orders',
  INVENTORY = 'inventory',
  CUSTOMERS = 'customers',
  SUPPLIERS = 'suppliers',
  FINANCIAL = 'financial',
  PERFORMANCE = 'performance',
}

/**
 * Report formats
 */
export enum ReportFormat {
  PDF = 'pdf',
  CSV = 'csv',
  EXCEL = 'excel',
  JSON = 'json',
}

/**
 * Frequency intervals
 */
export enum Frequency {
  ONCE = 'once',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  YEARLY = 'yearly',
}

/**
 * Data export formats
 */
export enum ExportFormat {
  CSV = 'csv',
  EXCEL = 'excel',
  JSON = 'json',
  PDF = 'pdf',
}

/**
 * Sort directions
 */
export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

/**
 * Filter operators
 */
export enum FilterOperator {
  EQUALS = 'eq',
  NOT_EQUALS = 'ne',
  GREATER_THAN = 'gt',
  GREATER_THAN_EQUAL = 'gte',
  LESS_THAN = 'lt',
  LESS_THAN_EQUAL = 'lte',
  IN = 'in',
  NOT_IN = 'nin',
  CONTAINS = 'contains',
  STARTS_WITH = 'startsWith',
  ENDS_WITH = 'endsWith',
  IS_NULL = 'isNull',
  IS_NOT_NULL = 'isNotNull',
}

/**
 * Chart types
 */
export enum ChartType {
  LINE = 'line',
  BAR = 'bar',
  PIE = 'pie',
  DOUGHNUT = 'doughnut',
  AREA = 'area',
  SCATTER = 'scatter',
  HISTOGRAM = 'histogram',
}

/**
 * Dashboard widget types
 */
export enum WidgetType {
  METRIC = 'metric',
  CHART = 'chart',
  TABLE = 'table',
  LIST = 'list',
  MAP = 'map',
  CALENDAR = 'calendar',
}

/**
 * Audit actions
 */
export enum AuditAction {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  VIEW = 'view',
  LOGIN = 'login',
  LOGOUT = 'logout',
  EXPORT = 'export',
  IMPORT = 'import',
}
